import React, { useState, useEffect, useRef } from 'react';
import useApiCaller from '../../hooks/use-api-caller';
import Loading from '.././Loading';
import 'react-toastify/dist/ReactToastify.css';
import { toast } from 'react-toastify';
import type { IDigestAlertPreference, ISmartAlertPreference, ICustomAlertPreference, AlertTypeKey } from '../../types/api-caller';
import { superuserPermissionAtom } from '../../store/config';
import { useRecoilValue } from 'recoil';
import { NavLink } from 'react-router-dom';
import TabSelector from '.././TabSelector';
import ToggleSwitch from '.././ToggleSwitch';

import DigestSettings, { DigestSettingsHandle } from './DigestSettings';
import SmartAlertsSettings, { SmartAlertsSettingsHandle } from './SmartAlertsSettings';
import CustomAlertsSettings, { CustomAlertsSettingsHandle } from './CustomAlertsSettings';

const EmailDeliverySelector = () => {
  const apiCaller = useApiCaller();

  const is_superuser = useRecoilValue(superuserPermissionAtom);
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [selectedTab, setSelectedTab] = useState<string>('digest');

  const [dirtyAlertTypes, setDirtyAlertTypes] = useState<AlertTypeKey[]>([]);

  // Global Alerts master toggle (for both Smart + Custom)
  const [alertsEnabled, setAlertsEnabled] = useState<boolean>(true);

  // For cross-coupling logic (when last alert type is turned off)
  const [smartSectionEnabled, setSmartSectionEnabled] = useState<boolean>(true);
  const [customSectionEnabled, setCustomSectionEnabled] = useState<boolean>(true);

  // Initial preferences fetched from API, passed to children
  const [initialDigestPreference, setInitialDigestPreference] = useState<IDigestAlertPreference | null>(null);
  const [initialSmartPreference, setInitialSmartPreference] = useState<ISmartAlertPreference | null>(null);
  const [initialCustomPreferences, setInitialCustomPreferences] = useState<ICustomAlertPreference[]>([]);

  // Child refs (imperative API to build payloads / force enable, etc.)
  const digestRef = useRef<DigestSettingsHandle | null>(null);
  const smartRef = useRef<SmartAlertsSettingsHandle | null>(null);
  const customRef = useRef<CustomAlertsSettingsHandle | null>(null);

  const markDirty = (type: AlertTypeKey) => {
    setDirtyAlertTypes((prev) => (prev.includes(type) ? prev : [...prev, type]));
  };

  const toggleShowAll = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    setShowAll((prev) => !prev);
  };

  const loadEmailPreferences = async () => {
    try {
      setLoading(true);

      const response = await apiCaller.getEmailPreferences(); // IUserEmailPreferencesResponse

      if (response.digest) {
        setInitialDigestPreference(response.digest);
      } else {
        setInitialDigestPreference(null);
      }

      let smartEnabledFromApi = false;
      if (response.smart) {
        setInitialSmartPreference(response.smart);
        smartEnabledFromApi = !!response.smart.enabled;
      } else {
        setInitialSmartPreference(null);
      }

      let customEnabledFromApi = false;
      if (response.custom && response.custom.length > 0) {
        setInitialCustomPreferences(response.custom);
        if (response.custom.some((res) => res.enabled === true)) {
          customEnabledFromApi = true;
        }
      } else {
        setInitialCustomPreferences([]);
      }

      setSmartSectionEnabled(smartEnabledFromApi);
      setCustomSectionEnabled(customEnabledFromApi);
      setAlertsEnabled(smartEnabledFromApi || customEnabledFromApi);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const saveEmailPreferences = async () => {
    if (dirtyAlertTypes.length === 0) return;

    try {
      for (const type of dirtyAlertTypes) {
        if (type === 'DIGEST') {
          const payload = digestRef.current?.buildPayload();
          if (payload) {
            await apiCaller.saveEmailPreferences('DIGEST', payload);
            setInitialDigestPreference(payload);
          }
        } else if (type === 'SMART') {
          const payload = smartRef.current?.buildPayload();
          if (payload) {
            await apiCaller.saveEmailPreferences('SMART', payload);
            setInitialSmartPreference(payload);
          }
        } else if (type === 'CUSTOM') {
          const payload = customRef.current?.buildPayload();
          if (payload) {
            await apiCaller.saveEmailPreferences('CUSTOM', payload);
            setInitialCustomPreferences(payload);
          }
        }
      }
    } catch (error: any) {
      toast.error('Not Saved');
      console.log('Error saving data:', error?.response?.data ?? error);
      await loadEmailPreferences();
    } finally {
      setDirtyAlertTypes([]);
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmailPreferences();
  }, []);

  useEffect(() => {
    if (loading) return;
    if (dirtyAlertTypes.length === 0) return;
    saveEmailPreferences();
  }, [dirtyAlertTypes, loading]);

  function handleAlertsEnabledChange(checked: boolean): void {
    setAlertsEnabled(checked);

    if (checked) {
      if (smartRef.current) {
        smartRef.current.forceEnable();
        setSmartSectionEnabled(true);
      }
    } else {
      if (customRef.current) {
        customRef.current.forceDisableAll();
      }
    }

    markDirty('SMART');
  }

  const handleSmartSectionEnabledChange = (enabled: boolean) => {
    setSmartSectionEnabled(enabled);
    if (!enabled && !customSectionEnabled) {
      setAlertsEnabled(false);
    }
  };

  const handleCustomSectionEnabledChange = (enabled: boolean) => {
    setCustomSectionEnabled(enabled);
    if (!enabled && !smartSectionEnabled) {
      setAlertsEnabled(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        Here you can set the nature and frequency of your email notifications. You can customise your alerts to ensure you're notified about
        activity that's of most interest to you.{' '}
        {showAll ? (
          <div className="mt-4">
            The email will show: the Company names with FSS Score changes; the current FSS Score for the company; and, the Percentage change
            in the companies' FSS Scores. If there is no change to a company’s FSS Score they will not be shown in the email. A maximum of
            20 companies trending negative and 20 companies trending positive will be shown in each email, prioritised by the biggest
            percentage changes.
            <div className="mt-4">
              <strong>Alert Settings</strong>
              <ul className="list-disc list-inside mt-2">
                <li>Choose which of your Lists to base your alerts on (you must create a List before you can use this feature)</li>
                <li>Choose whether to receive Daily or Weekly alerts (or both)</li>
                <li>You can create as many alerts as you like</li>
                <li>
                  In any case you can choose to include or exclude alerts based on Low Article volumes. This can help limit potential false
                  positives which can occur when {'<'}25 articles on a given entity are available over a 6 month period.
                </li>
              </ul>
            </div>
          </div>
        ) : (
          ''
        )}{' '}
        <button onClick={toggleShowAll} className="text-ow-blue hover:text-ow-secondary focus:outline-none">
          {showAll ? 'Show less' : 'Show more'}
        </button>
      </div>

      {loading && <Loading />}

      {!loading && (
        <div className="flex flex-col gap-4">
          <TabSelector
            options={[
              { label: 'Digest', value: 'digest' },
              { label: 'Alerts', value: 'alerts' },
            ]}
            selected={selectedTab}
            onChange={setSelectedTab}
          />

          {/* Digest tab */}
          {selectedTab === 'digest' && (
            <DigestSettings ref={digestRef} initialPreference={initialDigestPreference} onDirty={() => markDirty('DIGEST')} />
          )}

          {/* Alerts tab */}
          {selectedTab === 'alerts' && (
            <div className="flex flex-col">
              <div className="flex flex-row justify-between items-center gap-4">
                <div>Get notified when there is a change in the fss score for any company in your Portfolio or Lists.</div>
                <ToggleSwitch checked={alertsEnabled} onChange={handleAlertsEnabledChange} />
              </div>

              {/* Smart & Custom are always mounted for state & payload, but render content only when alertsEnabled is true */}
              <div>
                <SmartAlertsSettings
                  ref={smartRef}
                  initialPreference={initialSmartPreference}
                  alertsEnabled={alertsEnabled}
                  onDirty={() => markDirty('SMART')}
                  onSmartEnabledChange={handleSmartSectionEnabledChange}
                />
                <CustomAlertsSettings
                  ref={customRef}
                  initialPreferences={initialCustomPreferences}
                  alertsEnabled={alertsEnabled}
                  onDirty={() => markDirty('CUSTOM')}
                  onCustomEnabledChange={handleCustomSectionEnabledChange}
                />
              </div>
            </div>
          )}
          {is_superuser && (
            <div className="mt-6">
              <hr />
              <div className="text-ow-primary text-lg font-bold">Subscription Editor</div>
              The email rate of other users can be maintained in the subscription editor page.
              <div className="mt-2">
                <NavLink to="/SubscriptionEditor">Go to Subscription Editor</NavLink>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EmailDeliverySelector;
